import React, { useMemo, useState } from "react";
import axios from "axios";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Trash2, CheckCircle2, Loader2, FileText, ShieldAlert, Receipt, FileDown } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import ShippingInfoModal, { ShippingInfoButton } from "../components/ShippingInfoModal";
import useDownloadPdf from "../hooks/useDownloadPdf";
import useToolPageCms from "../hooks/useToolPageCms";
import { ToolPageBanner, ToolPageCtaBand } from "../components/ToolPageCms";
import RelatedDestinations from "../components/RelatedDestinations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_CMS = {
  page_banner: {
    eyebrow: "Paklijst",
    title: "Paklijst",
    highlight: "online invullen",
    subtitle:
      "Vul uw paklijst online in. U ontvangt een bevestiging en wij verwerken uw zending sneller — geen handgeschreven lijsten meer.",
    badges: [
      { icon: "package", label: "Items + waarde + conditie" },
      { icon: "shield",  label: "Geen gevaarlijke goederen" },
      { icon: "receipt", label: "Optioneel factuur erbij" },
    ],
  },
  intro: {},
  cta_band: {
    title: "Paklijst klaar — volgende stap?",
    description: "Wilt u meteen boeken of pickup regelen? Wij staan klaar.",
    cta_primary_label: "Boekingsformulier",
    cta_primary_url: "/boekingsformulier",
    cta_secondary_label: "Bel ons",
    cta_secondary_url: "tel:+31488235999",
  },
};

const EMPTY_ITEM = { box_number: "", description: "", quantity: 1, value: "", condition: "gebruikt", note: "" };

export default function PaklijstPage() {
  const { t } = useI18n();
  const { data: cms } = useToolPageCms("paklijst", FALLBACK_CMS);
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "", email: "", phone: "",
    sender_address: "", sender_postcode: "", sender_city: "", sender_country: "Nederland",
    destination: "Curaçao", method: "zeevracht", w_number: "", note: "",
    recipient_name: "", recipient_company: "", recipient_email: "", recipient_phone: "",
    recipient_address: "", recipient_city: "", recipient_country: "Curaçao", recipient_instructions: "",
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM, box_number: "1" }]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  // Phase 18.8 — optional auto-generated value-declaration invoice.
  const [generateInvoice, setGenerateInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const invoicePdf = useDownloadPdf();

  const totalValue = useMemo(
    () => items.reduce((acc, it) => acc + (Number(it.value) || 0) * (Number(it.quantity) || 1), 0),
    [items],
  );
  const totalItems = useMemo(
    () => items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0),
    [items],
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addRow = () => setItems((arr) => [...arr, { ...EMPTY_ITEM, box_number: String(arr.length + 1) }]);
  const removeRow = (idx) => setItems((arr) => arr.length === 1 ? arr : arr.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = items
      .filter((it) => it.description && it.description.trim())
      .map((it) => ({
        ...it,
        quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
        value: it.value === "" ? null : Number(it.value),
      }));
    if (cleaned.length === 0) {
      setError(t("paklijst.min_one_row_error"));
      return;
    }
    if (!form.dangerous_goods_confirmed) {
      setError(t("dangerous_goods.checkbox_required_error"));
      return;
    }
    // Phase 18.8 — extra validation when the customer wants the invoice
    // generated. Required: every item has a description, quantity AND
    // value. Sender e-mail + address fields are already required upstream.
    if (generateInvoice) {
      const missing = cleaned.find(
        (it) => !it.description || it.value === null || it.value === undefined || it.value === "" || !Number(it.value),
      );
      if (missing) {
        setError(t("paklijst.invoice_items_value_required"));
        return;
      }
      if (!form.recipient_country) {
        setError(t("paklijst.invoice_recipient_country_required"));
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/packing-list`, {
        ...form, items: cleaned,
        generate_invoice: !!generateInvoice,
      });
      setDone(true);
      setInvoiceResult(r.data?.invoice || null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || t("common.try_again"));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvoice = () =>
    invoicePdf.download(
      async () => {
        if (!invoiceResult?.download_url) {
          throw new Error("missing download URL");
        }
        const r = await axios.get(`${BACKEND_URL}${invoiceResult.download_url}`, { responseType: "blob" });
        return {
          blob: new Blob([r.data], { type: "application/pdf" }),
          filename: `${invoiceResult.invoice_number}.pdf`,
        };
      },
    );

  return (
    <>
      <SEO title={`${t("paklijst.page_title")} | 5999Cargo`} description={t("paklijst.page_subtitle")} />
      <ShippingInfoModal autoOpen />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="paklijst-page">
        <ToolPageBanner
          banner={cms?.page_banner || FALLBACK_CMS.page_banner}
          image="https://images.unsplash.com/photo-1573552991725-c7b115591d04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          imageAlt="Warehouse met pakketten — paklijst online invullen"
          eyebrowIcon={FileText}
          testid="paklijst-hero"
          extras={
            <ShippingInfoButton testId="paklijst-shipping-info-trigger" className="text-white border-white/30 hover:border-white" />
          }
        />

        <section className="py-14">
          <div className="container-narrow">
            {done ? (
              <div className="space-y-4" data-testid="paklijst-success">
                <div className="card-premium p-8 border-l-4 border-l-emerald-500">
                  <div className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6" />
                    </span>
                    <div>
                      <h2 className="font-chivo font-bold text-2xl text-emerald-900">{t("paklijst.success_title")}</h2>
                      <p className="text-[15px] text-emerald-800 mt-1.5 leading-relaxed">{t("paklijst.success_desc")}</p>
                    </div>
                  </div>
                </div>

                {/* Phase 18.8 — invoice download block */}
                {invoiceResult?.status === "generated" && (
                  <div className="card-premium p-6 border-l-4 border-l-brand-500" data-testid="paklijst-invoice-success">
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
                        <Receipt className="w-6 h-6" />
                      </span>
                      <div className="flex-1">
                        <h3 className="font-chivo font-bold text-lg text-brand-900">{t("paklijst.invoice_generated_title")}</h3>
                        <p className="text-sm text-ink-600 mt-1">{t("paklijst.invoice_generated_intro")}</p>
                        <div className="mt-3 inline-flex items-center gap-2 bg-brand-50 border border-brand-100 rounded-lg px-3 py-1.5">
                          <span className="text-[10px] uppercase tracking-[0.18em] font-chivo font-bold text-brand-500">{t("paklijst.invoice_number_label")}</span>
                          <span className="font-mono text-sm font-bold text-brand-900" data-testid="paklijst-invoice-number">{invoiceResult.invoice_number}</span>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <Button
                            onClick={downloadInvoice}
                            disabled={invoicePdf.loading}
                            className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-chivo font-bold uppercase text-xs px-5 py-2.5"
                            data-testid="paklijst-invoice-download"
                          >
                            {invoicePdf.loading
                              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("paklijst.invoice_downloading")}</>
                              : <><FileDown className="w-4 h-4 mr-2" /> {t("paklijst.invoice_download_btn")}</>}
                          </Button>
                          {invoicePdf.loading && invoicePdf.isSlow && (
                            <span className="text-xs text-ink-500">{invoicePdf.slowHint}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {invoiceResult?.status === "failed" && (
                  <div className="card-premium p-6 border-l-4 border-l-amber-500" data-testid="paklijst-invoice-failed">
                    <h3 className="font-chivo font-bold text-amber-900">{t("paklijst.invoice_failed_title")}</h3>
                    <p className="text-sm text-amber-800 mt-1">{invoiceResult.error || t("paklijst.invoice_failed_generic")}</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="paklijst-form">
                {/* Verzender */}
                <div className="card-premium p-6 sm:p-8" data-testid="paklijst-sender-block">
                  <h2 className="font-chivo font-bold text-xl text-brand-900 mb-5">{t("forms.sender_block_title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>{t("forms.first_name")} *</Label><Input data-testid="paklijst-first-name" required value={form.first_name} onChange={set("first_name")} className="rounded-xl" /></div>
                    <div><Label>Achternaam *</Label><Input data-testid="paklijst-last-name" required value={form.last_name} onChange={set("last_name")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>Bedrijfsnaam (optioneel)</Label><Input data-testid="paklijst-company" value={form.company_name} onChange={set("company_name")} className="rounded-xl" /></div>
                    <div><Label>E-mailadres *</Label><Input data-testid="paklijst-email" required type="email" value={form.email} onChange={set("email")} className="rounded-xl" /></div>
                    <div><Label>Telefoonnummer *</Label><Input data-testid="paklijst-phone" required value={form.phone} onChange={set("phone")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.sender_address")}</Label><Input data-testid="paklijst-sender-address" value={form.sender_address} onChange={set("sender_address")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.postcode")}</Label><Input data-testid="paklijst-sender-postcode" value={form.sender_postcode} onChange={set("sender_postcode")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.city")}</Label><Input data-testid="paklijst-sender-city" value={form.sender_city} onChange={set("sender_city")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.country")}</Label><Input data-testid="paklijst-sender-country" value={form.sender_country} onChange={set("sender_country")} className="rounded-xl" /></div>
                  </div>
                </div>

                {/* Ontvanger */}
                <div className="card-premium p-6 sm:p-8" data-testid="paklijst-receiver-block">
                  <h2 className="font-chivo font-bold text-xl text-brand-900 mb-5">{t("forms.receiver_block_title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>{t("forms.recipient_name")} *</Label><Input data-testid="paklijst-recipient-name" required value={form.recipient_name} onChange={set("recipient_name")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.company_optional")}</Label><Input data-testid="paklijst-recipient-company" value={form.recipient_company} onChange={set("recipient_company")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.recipient_email_optional")}</Label><Input data-testid="paklijst-recipient-email" type="email" value={form.recipient_email} onChange={set("recipient_email")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.phone")} *</Label><Input data-testid="paklijst-recipient-phone" required value={form.recipient_phone} onChange={set("recipient_phone")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.destination_address")} *</Label><Input data-testid="paklijst-recipient-address" required value={form.recipient_address} onChange={set("recipient_address")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.destination_city")}</Label><Input data-testid="paklijst-recipient-city" value={form.recipient_city} onChange={set("recipient_city")} className="rounded-xl" /></div>
                    <div><Label>{t("forms.destination_country")} *</Label><Input data-testid="paklijst-recipient-country" required value={form.recipient_country} onChange={set("recipient_country")} className="rounded-xl" /></div>
                    <div className="sm:col-span-2"><Label>{t("forms.extra_instructions_optional")}</Label><Textarea data-testid="paklijst-recipient-instructions" rows={2} value={form.recipient_instructions} onChange={set("recipient_instructions")} className="rounded-xl" /></div>
                  </div>
                </div>

                {/* Zending */}
                <div className="card-premium p-6 sm:p-8">
                  <h2 className="font-chivo font-bold text-xl text-brand-900 mb-5">{t("forms.shipment_block_title")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>{t("forms.destination")} *</Label><Input data-testid="paklijst-destination" required value={form.destination} onChange={set("destination")} className="rounded-xl" /></div>
                    <div>
                      <Label>{t("forms.shipment_method")}</Label>
                      <select data-testid="paklijst-method" value={form.method} onChange={set("method")} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm h-10">
                        <option value="zeevracht">{t("paklijst.method_zee")}</option>
                        <option value="luchtvracht">{t("paklijst.method_lucht")}</option>
                        <option value="shop-drop-ship">{t("paklijst.method_shop_drop_ship")}</option>
                        <option value="verhuizing">{t("paklijst.method_verhuizing")}</option>
                      </select>
                    </div>
                    <div><Label>{t("forms.w_number_optional")}</Label><Input data-testid="paklijst-wnumber" value={form.w_number} onChange={set("w_number")} placeholder="W-xxxxx" className="rounded-xl font-mono" /></div>
                    <div></div>
                    <div className="sm:col-span-2"><Label>{t("forms.remark")}</Label><Textarea data-testid="paklijst-note" rows={2} value={form.note} onChange={set("note")} className="rounded-xl" /></div>
                  </div>
                </div>

                {/* Items */}
                <div className="card-premium p-6 sm:p-8">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <h2 className="font-chivo font-bold text-xl text-brand-900">Paklijst items</h2>
                    <div className="text-xs text-ink-500">
                      Totaal: <span className="font-mono-num font-bold text-brand-900">{totalItems}</span> items · €
                      <span className="font-mono-num font-bold text-brand-900"> {totalValue.toFixed(2)}</span>
                    </div>
                  </div>

                  <p
                    className="text-[12px] text-ink-600 leading-relaxed mb-3 px-1"
                    data-testid="paklijst-items-instruction"
                  >
                    Gebruik <span className="font-semibold text-brand-900">één regel per artikel</span> of artikelgroep.
                    Voeg extra regels toe voor extra artikelen — zet geen verschillende artikelen op één omschrijving.
                  </p>

                  <div className="space-y-2.5">
                    {items.map((it, idx) => {
                      const descRaw = it.description || "";
                      const descLen = descRaw.length;
                      const commaCount = (descRaw.match(/,/g) || []).length;
                      const looksLikeMultiArticle = commaCount > 2 || descLen > 120;
                      return (
                      <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5" data-testid={`paklijst-row-${idx}`}>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                          <div className="md:col-span-2"><Label className="text-[10px] uppercase">Artikel #</Label><Input data-testid={`paklijst-row-box-${idx}`} value={it.box_number} onChange={(e) => setItem(idx, "box_number", e.target.value)} className="rounded-xl" /></div>
                          <div className="md:col-span-5">
                            <Label className="text-[10px] uppercase">Omschrijving *</Label>
                            <Input
                              data-testid={`paklijst-row-desc-${idx}`}
                              required
                              value={it.description}
                              placeholder="Bijv. gebruikte kleding"
                              onChange={(e) => setItem(idx, "description", e.target.value)}
                              className="rounded-xl"
                            />
                            {looksLikeMultiArticle && (
                              <div
                                className="mt-1 text-[11px] text-amber-700 leading-snug"
                                data-testid={`paklijst-row-multi-article-warning-${idx}`}
                              >
                                Deze omschrijving lijkt meerdere artikelen te bevatten. Voor een duidelijke paklijst raden wij aan om ieder artikel of artikelgroep op een aparte regel te zetten.
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-1"><Label className="text-[10px] uppercase">Aantal *</Label><Input data-testid={`paklijst-row-qty-${idx}`} required type="number" min="1" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="rounded-xl" /></div>
                          <div className="md:col-span-2"><Label className="text-[10px] uppercase">Waarde (€)</Label><Input data-testid={`paklijst-row-value-${idx}`} type="number" step="0.01" min="0" value={it.value} onChange={(e) => setItem(idx, "value", e.target.value)} className="rounded-xl" /></div>
                          <div className="md:col-span-1">
                            <Label className="text-[10px] uppercase">Conditie</Label>
                            <select value={it.condition} onChange={(e) => setItem(idx, "condition", e.target.value)} className="w-full rounded-xl border border-gray-300 bg-white px-2 py-2 text-xs h-10">
                              <option value="nieuw">Nieuw</option>
                              <option value="gebruikt">Gebruikt</option>
                            </select>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <Button type="button" size="icon" variant="outline" className="rounded-xl text-red-600 w-full md:w-auto" onClick={() => removeRow(idx)} disabled={items.length === 1} data-testid={`paklijst-row-remove-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>

                  <Button type="button" onClick={addRow} variant="outline" className="rounded-xl mt-3" data-testid="paklijst-add-row">
                    <Plus className="w-4 h-4 mr-1" /> {t("paklijst.add_row")}
                  </Button>
                </div>

                {/* Phase 18.8 — Optional invoice/value-declaration */}
                <div
                  className="rounded-2xl bg-brand-50 border-l-4 border-brand-500 p-5 sm:p-6"
                  data-testid="paklijst-invoice-block"
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateInvoice}
                      onChange={(e) => setGenerateInvoice(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-brand-600"
                      data-testid="paklijst-generate-invoice-checkbox"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-brand-500" />
                        <span className="font-chivo font-bold text-sm text-brand-900">{t("paklijst.invoice_option_label")}</span>
                      </div>
                      <p className="text-xs text-ink-600 mt-1 leading-relaxed">
                        {t("paklijst.invoice_option_help")}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Dangerous Goods waarschuwing + verplichte akkoord */}
                <div className="rounded-2xl bg-red-50 border-l-4 border-red-500 p-5 sm:p-6" data-testid="paklijst-dangerous-goods-block">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-chivo font-bold text-base text-red-800">{t("dangerous_goods.paklijst_warning_title")}</h3>
                      <p className="text-sm text-red-700 mt-1.5 leading-relaxed">
                        {t("dangerous_goods.warning_intro")}{" "}
                        <a href="/documenten#dangerous-goods" target="_blank" rel="noreferrer" className="underline font-bold">{t("dangerous_goods.full_list_link")}</a>.
                      </p>
                      <label className="flex items-start gap-2 mt-4 text-sm text-red-900 cursor-pointer">
                        <input
                          type="checkbox"
                          required
                          data-testid="paklijst-dangerous-goods-checkbox"
                          checked={form.dangerous_goods_confirmed}
                          onChange={(e) => setForm((s) => ({ ...s, dangerous_goods_confirmed: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 accent-red-700"
                        />
                        <span>
                          <span className="font-bold">{t("dangerous_goods.required_prefix")}:</span> {t("dangerous_goods.checkbox_label")}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {error && (
                  <div data-testid="paklijst-error" className="rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 text-sm">{error}</div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-chivo font-bold uppercase text-sm px-8 py-6 ml-auto shadow-brand-soft hover:shadow-brand-glow active:scale-[0.98] transition-all" data-testid="paklijst-submit">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("paklijst.submitting")}</> : t("paklijst.submit")}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>
        <ToolPageCtaBand cta={cms?.cta_band || FALLBACK_CMS.cta_band} testid="paklijst-cta-band" />
        <RelatedDestinations
          testid="paklijst-related"
          eyebrow="Paklijst per bestemming"
          title="Paklijsten voor verzending naar de Caribbean"
          description="Per bestemming kunnen documentatie en waarde-opgave variëren. Bekijk wat wij naar elke bestemming verzorgen — wij begeleiden u door de juiste paklijst."
          anchorTemplate="Paklijst voor zending naar {name}"
        />
      </main>
    </>
  );
}
